const LETTERS='CDEFGAB', NATURAL=[0,2,4,5,7,9,11];
export const SCALE_FORMS={major:'Major',naturalMinor:'Natural minor',harmonicMinor:'Harmonic minor',melodicMinor:'Melodic minor (ascending)'};
const PATTERNS={major:[0,2,4,5,7,9,11,12],naturalMinor:[0,2,3,5,7,8,10,12],harmonicMinor:[0,2,3,5,7,8,11,12],melodicMinor:[0,2,3,5,7,9,11,12]};
export function isCompositeStaffScale(config){return !!config.scaleLength && !!config.grading?.halfSteps && !!config.grading?.accidentals && !!config.grading?.keySignatureAccidentals;}
export function staffScaleSettings(config){
 const label=String(config.scaleLabel || config.scaleTitle || 'C Major').replaceAll('♯','#').replaceAll('♭','b');
 const tonic=config.editorScaleTonic || label.match(/^\s*([A-Ga-g][#b]?)/)?.[1] || 'C';
 const form=config.editorScaleForm || (/harmonic/i.test(label)?'harmonicMinor':/melodic/i.test(label)?'melodicMinor':/minor/i.test(label)?'naturalMinor':'major');
 const first=config.givenNotes?.[1]?.pitches?.[0] || (config.clef==='bass'?'C3':'C4');
 return {tonic:tonic[0].toUpperCase()+tonic.slice(1),form,octave:Number(first.match(/(-?\d+)$/)?.[1] ?? 4)};
}
function spelling(tonic,form){
 const m=String(tonic).trim().replaceAll('♯','#').replaceAll('♭','b').match(/^([A-Ga-g])([#b]?)$/);
 if(!m || !PATTERNS[form]) throw Error('Enter a tonic such as F♯, B♭, or C, and select the scale form.');
 const start=LETTERS.indexOf(m[1].toUpperCase()),root=NATURAL[start]+(m[2]==='#'?1:m[2]==='b'?-1:0);
 return PATTERNS[form].map((step,i)=>{
  const index=(start+i)%7,octaveOffset=Math.floor((start+i)/7);
  const delta=root+step-NATURAL[index]-12*octaveOffset;
  if(Math.abs(delta)>2)throw Error('This spelling requires accidentals beyond double sharps or flats.');
  return {letter:LETTERS[index],accidental:delta>0?'#'.repeat(delta):'b'.repeat(-delta),octaveOffset};
 });
}
export function calculateStaffScale(tonic,form,clef='treble',octave=4){
 if(!['treble','bass'].includes(clef))throw Error('Choose treble or bass clef.');
 if(!Number.isInteger(Number(octave)) || Number(octave)<0 || Number(octave)>7)throw Error('Starting octave must be between 0 and 7.');
 const spelled=spelling(tonic,form),base=spelling(tonic,form==='major'?'major':'naturalMinor').slice(0,7);
 const changes=base.filter(n=>n.accidental);
 const sign=changes[0]?.accidental || '';
 if(changes.some(n=>n.accidental!==sign) || !['','#','b'].includes(sign))throw Error('Choose a key with a standard key signature of up to seven sharps or flats.');
 const signatureOrder=sign==='#'?'FCGDAEB':'BEADGCF';
 const positions={treble:{'#':['F5','C5','G5','D5','A4','E5','B4'],b:['B4','E5','A4','D5','G4','C5','F4']},bass:{'#':['F3','C3','G3','D3','A2','E3','B2'],b:['B2','E3','A2','D3','G2','C3','F2']}};
 const signature=Array.from({length:changes.length},(_,i)=>{
  if(!changes.some(n=>n.letter===signatureOrder[i]))throw Error('The scale does not have a standard key signature.');
  return {pitch:positions[clef][sign][i],accidental:sign};
 });
 const givenNotes={},accidentals={},givenScaleAccidentalSlots=[];
 spelled.forEach((n,i)=>{
  const slot=i+1,givenTonic=(i===0||i===7)&&!!n.accidental;
  givenNotes[slot]={symbol:'whole_note',pitches:[n.letter+(givenTonic?n.accidental:'')+(Number(octave)+n.octaveOffset)]};
  if(givenTonic)givenScaleAccidentalSlots.push(slot);else accidentals[slot]=n.accidental;
 });
 const halfSteps=PATTERNS[form].slice(1).flatMap((step,i)=>step-PATTERNS[form][i]===1?[`${i+1}-${i+2}`]:[]);
 return {givenNotes,accidentals,halfSteps,keySignatureAccidentals:{1:signature},givenScaleAccidentalSlots};
}
export function applyStaffScale(config,{tonic,form,clef=config.clef || 'treble',octave}){
 const calculated=calculateStaffScale(tonic,form,clef,octave);
 const displayedTonic=tonic.trim().replaceAll('#','♯').replaceAll('b','♭');
 const labelTonic=(form==='major'?displayedTonic[0].toUpperCase():displayedTonic[0].toLowerCase())+displayedTonic.slice(1);
 config.scaleLabel=`${labelTonic} ${form==='major'?'Major':form==='naturalMinor'?'minor':form==='harmonicMinor'?'harmonic minor':'melodic minor'}`;
 config.editorScaleForm=form;config.editorScaleAutomatic=true;config.clef=clef;
 config.scaleLength=8;config.slotCount=9;config.keySignatureSlot=8;
 config.givenNotes=calculated.givenNotes;config.givenScaleAccidentalSlots=calculated.givenScaleAccidentalSlots;
 config.grading={...config.grading,halfSteps:calculated.halfSteps,accidentals:calculated.accidentals,keySignatureAccidentals:calculated.keySignatureAccidentals};
 const tonicName=calculated.givenNotes[1].pitches[0].replace(/-?\d+$/,'');
 if(config.grading.tonic)config.grading.tonic=tonicName;
 if(config.grading.tonicName)config.grading.tonicName=tonicName;
 if(config.grading.dominantName){const n=spelling(tonic,form)[4];config.grading.dominantName=n.letter+n.accidental;}
 // Keep the existing rubric weights and update any scale-form wording.
 const formWords=form==='major'?'major':form==='naturalMinor'?'natural minor':form==='harmonicMinor'?'harmonic minor':'ascending melodic minor';
 function wording(value){
  if(typeof value==='string')return value.replace(/\b(?:major|(?:(?:natural|harmonic|ascending melodic|melodic) )?minor) scale\b/gi,`${formWords} scale`);
  if(Array.isArray(value))return value.map(wording);
  if(value&&typeof value==='object')return Object.fromEntries(Object.entries(value).map(([k,v])=>[k,wording(v)]));
  return value;
 }
 if(config.prompt)config.prompt=wording(config.prompt);
 return config;
}

export function scalePlacementSettings(config){
 const settings=staffScaleSettings(config);
 const first=config.grading?.scalePlacement?.notes?.[1]||config.grading?.scalePlacement?.writtenPitches?.[1];
 if(first)settings.octave=Number(first.match(/(-?\d+)$/)?.[1]??settings.octave);
 settings.direction=config.editorScaleDirection || (Number(config.scaleLength)===15?'ascendingDescending':'ascending');
 return settings;
}
export function applyScalePlacement(config,settings){
 const {tonic,form,clef=config.clef||'treble',octave}=settings;
 const direction=settings.direction || 'ascending';
 const calculated=calculateStaffScale(tonic,direction==='descending'&&form==='melodicMinor'?'naturalMinor':form,clef,direction==='descending'?Number(octave)-1:Number(octave));
 const notes={},writtenPitches={};
 for(const [slot,given] of Object.entries(calculated.givenNotes)){
  const pitch=given.pitches[0],match=pitch.match(/^([A-G])([#b]*)(\d+)$/);
  const targetSlot=direction==='descending'?9-Number(slot):Number(slot);
  notes[targetSlot]=match[1]+match[3];
  const accidental=calculated.accidentals[slot]??match[2];
  writtenPitches[targetSlot]=match[1]+accidental+match[3];
 }
 if(direction==='ascendingDescending'){
  const descending=calculateStaffScale(tonic,form==='melodicMinor'?'naturalMinor':form,clef,Number(octave));
  for(let degree=7;degree>=1;degree--){
   const sourceSlot=String(degree),targetSlot=16-degree;
   const match=descending.givenNotes[sourceSlot].pitches[0].match(/^([A-G])([#b]*)(\d+)$/);
   notes[targetSlot]=match[1]+match[3];
   writtenPitches[targetSlot]=match[1]+(descending.accidentals[sourceSlot]??match[2])+match[3];
  }
 }
 const length=direction==='ascendingDescending'?15:8;
 const labelTonic=tonic.trim().replaceAll('#','♯').replaceAll('b','♭');
 config.scaleLabel=(form==='major'?labelTonic[0].toUpperCase():labelTonic[0].toLowerCase())+labelTonic.slice(1)+' '+(form==='major'?'Major':form==='naturalMinor'?'natural minor':form==='harmonicMinor'?'harmonic minor':'melodic minor');
 config.editorScaleForm=form;config.editorScaleTonic=tonic;config.editorScaleDirection=direction;config.editorScalePlacementAutomatic=true;config.clef=clef;
 config.inputMode='placeNotes';config.scaleLength=length;config.measures=length;config.slotCount=length;config.slotsPerBar=1;config.showTonic=false;
 config.allowKeySignatureInput=true;config.keySignatureInputPosition='start';
 config.staffInputPolicy={...config.staffInputPolicy,allowNoteInput:true,allowAccidentalInput:true,allowHalfStepInput:false,accidentalInputMode:'palette'};
 config.allowedSymbols=['whole_note'];config.renderBarlines=config.renderBarlines ?? false;
 config.givenNotes={};if(!config.givenScaleDegree){delete config.givenScaleAccidentalSlots;delete config.inputSlots;}
 if(config.givenScaleDegree){
  const degree=Number(config.givenScaleDegree),slot=direction==='descending'?9-degree:degree;
  if(slot>=1&&slot<=8){
   config.givenNotes[slot]={symbol:'whole_note',pitches:[writtenPitches[slot]]};
   config.givenScaleAccidentalSlots=[slot];
   config.inputSlots=Array.from({length},(_,i)=>i+1).filter(n=>n!==slot);
  }
 }
 if(config.scaleNameInput){
  const formLabel=form==='harmonicMinor'?'harmonic minor':form==='melodicMinor'?'melodic minor':form==='naturalMinor'?'natural minor':'major';
  config.scaleNameInput={...config.scaleNameInput,suffix:`${formLabel} ${direction}`};
  delete config.scaleLabel;
 }

 config.grading={...config.grading,scalePlacement:{...config.grading?.scalePlacement,keySignatureAccidentals:calculated.keySignatureAccidentals,notes,writtenPitches}};
 if(config.scaleNameInput)config.grading.scalePlacement.tonicName=tonic;
 delete config.grading.halfSteps;delete config.grading.accidentals;delete config.grading.keySignatureAccidentals;
 // Preserve the three existing section weights.
 config.gradingPoints={keySignatureAccidentals:1,notes:1,accidentals:1,...config.gradingPoints};
 function wording(v){
  if(typeof v==='string')return v.replace(/\b(?:major|(?:(?:natural|harmonic|melodic) )?minor) scale\b/gi,`${form==='major'?'major':'minor'} scale`);
  if(Array.isArray(v))return v.map(wording);
  if(v&&typeof v==='object')return Object.fromEntries(Object.entries(v).map(([k,value])=>[k,wording(value)]));
  return v;
 }
 if(config.prompt)config.prompt=wording(config.prompt);
 return config;
}
export function scalePlacementAddedAccidentals(config){
 const rubric=config.grading.scalePlacement,signature=Object.fromEntries((rubric.keySignatureAccidentals[1]||[]).map(n=>[n.pitch[0],n.accidental]));
 return Object.entries(rubric.writtenPitches).flatMap(([slot,pitch])=>{
  const [,letter,accidental]=pitch.match(/^([A-G])([#b]*)(\d+)$/);
  return accidental===(signature[letter]||'')?[]:[{slot,pitch,accidental:accidental||'n'}];
 });
}
